import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Button,
    Grid,
    IconButton,
    Link,
    Tooltip,
    Typography
} from '@mui/material';
import { Add, DeleteOutline, HelpOutline } from '@mui/icons-material';

import { FullForm, TextField, useChangeable } from '../CommonComponents/Form';
import Loading from '../CommonComponents/Fetch/Loading';
import {
    createTenantArticle,
    deleteTenantArticle,
    getTenantArticle,
    publishTenantArticle,
    unpublishTenantArticle,
    updateTenantArticle,
    TenantArticle,
    TenantArticleSection
} from '../../api-admin/tenant-articles';
import { getTenant } from '../../api-admin/tenants';
import toast from '../../utils/toast';
import ArticleCoverHandler, { uploadArticleCover } from './ArticleCoverHandler';

type FormSection = TenantArticleSection & { key: string };

let sectionKeySeq = 0;

function nextSectionKey() {
    sectionKeySeq += 1;
    return `section-${sectionKeySeq}`;
}

const emptySection = (): FormSection => ({
    key: nextSectionKey(),
    subtitle: '',
    body: ''
});

function toFormSections(sections: TenantArticleSection[]): FormSection[] {
    return sections.map((section) => ({
        key: nextSectionKey(),
        subtitle: section.subtitle,
        body: section.body
    }));
}

const defaultEntity = {
    title: '',
    slug: '',
    excerpt: '',
    metaTitle: '',
    metaDescription: '',
    coverImageUrl: null as string | null,
    sections: [emptySection()],
    status: 'draft' as TenantArticle['status']
};

function articleToEntity(article: TenantArticle) {
    return {
        title: article.title,
        slug: article.slug,
        excerpt: article.excerpt || '',
        metaTitle: article.metaTitle || '',
        metaDescription: article.metaDescription || '',
        coverImageUrl: article.coverImageUrl,
        sections: article.sections?.length ? toFormSections(article.sections) : [emptySection()],
        status: article.status
    };
}

const SEO_TITLE_HELP_HREF = 'https://ahrefs.com/blog/title-tag-seo/?utm_source=detailed-extension&utm_content=tooltip';
const SEO_DESCRIPTION_HELP_HREF = 'https://ahrefs.com/seo/glossary/meta-description/?utm_source=detailed-extension&utm_content=tooltip';

function SeoHelpTooltip({
    label,
    text,
    href
}: {
    label: string;
    text: string;
    href: string;
}) {
    return (
        <Tooltip
            placement="top"
            enterDelay={200}
            leaveDelay={300}
            title={(
                <Typography variant="body2" component="span">
                    {text}
                    {' '}
                    <Link
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="always"
                        color="inherit"
                        onClick={(event) => event.stopPropagation()}
                    >
                        Läs mer
                    </Link>
                </Typography>
            )}
        >
            <Box
                component="span"
                aria-label={`Om ${label}`}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    ml: 0.5,
                    color: 'text.secondary',
                    cursor: 'help',
                    verticalAlign: 'middle'
                }}
            >
                <HelpOutline sx={{ fontSize: 16 }} />
            </Box>
        </Tooltip>
    );
}

function articleDraft(entity: typeof defaultEntity) {
    return {
        title: entity.title,
        slug: entity.slug,
        excerpt: entity.excerpt,
        metaTitle: entity.metaTitle,
        metaDescription: entity.metaDescription,
        sections: entity.sections.map(({ subtitle, body }) => ({ subtitle, body }))
    };
}

function inputFromEntity(entity: typeof defaultEntity) {
    const draft = articleDraft(entity);
    return {
        ...draft,
        metaTitle: draft.metaTitle || null,
        metaDescription: draft.metaDescription || null
    };
}

export default function TenantArticleForm() {
    const { tenantId, articleId } = useParams() as unknown as { tenantId: number; articleId?: string };
    const navigate = useNavigate();
    const isEdit = Boolean(articleId);
    const [entity, onChange] = useChangeable(defaultEntity);
    const [pendingCoverFile, setPendingCoverFile] = React.useState<File | null>(null);
    const [tenantName, setTenantName] = React.useState<string | null>(null);
    const [isLoading, setIsLoading] = React.useState(isEdit);
    const savedDraftRef = React.useRef(articleDraft(defaultEntity));
    const coverChangedRef = React.useRef(false);

    const rememberDraft = React.useCallback((next: typeof defaultEntity) => {
        savedDraftRef.current = articleDraft(next);
        coverChangedRef.current = false;
    }, []);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const tenant = await getTenant(tenantId) as { name?: string };
                if (!cancelled && tenant.name) {
                    setTenantName(tenant.name);
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [tenantId]);

    React.useEffect(() => {
        if (!articleId) {
            return undefined;
        }
        let cancelled = false;
        (async () => {
            try {
                const article = await getTenantArticle(tenantId, articleId);
                if (!cancelled) {
                    const next = articleToEntity(article);
                    rememberDraft(next);
                    onChange(next, { reset: true });
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
                toast(error, 'Kunde inte hämta artikeln');
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [articleId, tenantId, onChange, rememberDraft]);

    const setSections = React.useCallback((sections: FormSection[]) => {
        onChange({ sections });
    }, [onChange]);

    const onSubmit = React.useCallback(async () => {
        try {
            if (isEdit && articleId) {
                const updated = await updateTenantArticle(tenantId, articleId, inputFromEntity(entity));
                const next = articleToEntity(updated);
                rememberDraft(next);
                onChange(next, { reset: true });
                toast('Artikeln sparad');
            } else {
                const created = await createTenantArticle(tenantId, inputFromEntity(entity));
                let coverFailed = false;
                if (pendingCoverFile) {
                    try {
                        await uploadArticleCover(tenantId, created.id, pendingCoverFile);
                    } catch (error) {
                        coverFailed = true;
                        // eslint-disable-next-line no-console
                        console.error(error);
                        toast('Ett fel uppstod vid uppladdningen, försök igen senare');
                    }
                }
                if (!coverFailed) {
                    toast('Artikel skapad');
                }
                navigate(`/tenants/${tenantId}/articles/${created.id}/edit`, { replace: true });
            }
        } catch (e) {
            toast(e, `Kunde inte ${isEdit ? 'spara' : 'skapa'} artikeln, försök igen`);
        }
    }, [articleId, entity, isEdit, navigate, onChange, pendingCoverFile, rememberDraft, tenantId]);

    const onPublishToggle = React.useCallback(async () => {
        if (!articleId) return;
        try {
            await updateTenantArticle(tenantId, articleId, inputFromEntity(entity));
            const updated = entity.status === 'published'
                ? await unpublishTenantArticle(tenantId, articleId)
                : await publishTenantArticle(tenantId, articleId);
            const next = articleToEntity(updated);
            rememberDraft(next);
            onChange(next, { reset: true });
            toast(updated.status === 'published' ? 'Artikeln publicerad' : 'Artikeln avpublicerad');
        } catch (e) {
            toast(e, 'Kunde inte ändra publiceringsstatus');
        }
    }, [articleId, entity, onChange, rememberDraft, tenantId]);

    const onDelete = React.useCallback(async () => {
        if (!articleId) return;
        try {
            await deleteTenantArticle(tenantId, articleId);
            toast('Artikeln borttagen');
            navigate(`/tenants/${tenantId}`);
        } catch (e) {
            toast(e, 'Kunde inte ta bort artikeln');
        }
    }, [articleId, navigate, tenantId]);

    const hasChanges = coverChangedRef.current
        || Boolean(pendingCoverFile)
        || JSON.stringify(articleDraft(entity)) !== JSON.stringify(savedDraftRef.current);

    if (isLoading) {
        return <Loading name="artikeln" />;
    }

    const slugLocked = isEdit && entity.status === 'published';

    return (
        <FullForm
            title={isEdit ? 'Redigera artikel' : 'Ny artikel'}
            entity={entity}
            onSubmit={onSubmit}
            submitText="Spara"
            submitDisabled={!hasChanges}
            headerNote={tenantName ? `Detta är en artikel om ${tenantName}` : undefined}
            onAltSubmit={isEdit ? onPublishToggle : undefined}
            altSubmitText={entity.status === 'published' ? 'Avpublicera' : 'Publicera'}
            onDelete={isEdit ? onDelete : undefined}
        >
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Omslagsbild</Typography>
                    <ArticleCoverHandler
                        tenantId={tenantId}
                        articleId={articleId}
                        url={entity.coverImageUrl}
                        onUploaded={(coverImageUrl) => {
                            coverChangedRef.current = true;
                            onChange({ coverImageUrl });
                        }}
                        onFileSelected={(file) => {
                            coverChangedRef.current = true;
                            setPendingCoverFile(file);
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        name="title"
                        label="Titel"
                        value={entity.title}
                        onChange={onChange}
                        required
                        maxLength={200}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        name="slug"
                        label="Slug"
                        value={entity.slug}
                        onChange={onChange}
                        disabled={slugLocked}
                        helperText={slugLocked ? 'Slug kan inte ändras efter publicering' : 'Lämna tomt för att skapa från titeln'}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        name="excerpt"
                        label="Ingress"
                        value={entity.excerpt}
                        onChange={onChange}
                        multiline
                        minRows={2}
                        helperText="Används i listor och som meta-beskrivning om fältet nedan är tomt"
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        name="metaTitle"
                        label={(
                            <>
                                SEO-titel (valfri)
                                <SeoHelpTooltip
                                    label="SEO-titel"
                                    text="Visas som klickbar rubrik i sökresultat och i webbläsarfliken. Sikta på cirka 50–60 tecken."
                                    href={SEO_TITLE_HELP_HREF}
                                />
                            </>
                        )}
                        value={entity.metaTitle}
                        onChange={onChange}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        name="metaDescription"
                        label={(
                            <>
                                SEO-beskrivning (valfri)
                                <SeoHelpTooltip
                                    label="SEO-beskrivning"
                                    text="Kort sammanfattning som kan visas under rubriken i sökresultat. Sikta på cirka 150-160 tecken."
                                    href={SEO_DESCRIPTION_HELP_HREF}
                                />
                            </>
                        )}
                        value={entity.metaDescription}
                        onChange={onChange}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="subtitle2">Avsnitt</Typography>
                </Grid>
                {entity.sections.map((section: FormSection, index: number) => (
                    <Grid item xs={12} key={section.key}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Box sx={{ flex: 1 }}>
                                <TextField
                                    name={`section-subtitle-${index}`}
                                    label={`Underrubrik ${index + 1}`}
                                    value={section.subtitle}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const next = [...entity.sections];
                                        next[index] = { ...next[index], subtitle: e.target.value };
                                        setSections(next);
                                    }}
                                />
                                <TextField
                                    name={`section-body-${index}`}
                                    label={`Brödtext ${index + 1}`}
                                    value={section.body}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const next = [...entity.sections];
                                        next[index] = { ...next[index], body: e.target.value };
                                        setSections(next);
                                    }}
                                    multiline
                                    minRows={4}
                                />
                            </Box>
                            <IconButton
                                aria-label="Ta bort avsnitt"
                                onClick={() => {
                                    const next = entity.sections.filter((_: FormSection, i: number) => i !== index);
                                    setSections(next.length ? next : [emptySection()]);
                                }}
                                sx={{ mt: 1 }}
                            >
                                <DeleteOutline />
                            </IconButton>
                        </Box>
                    </Grid>
                ))}
                <Grid item xs={12}>
                    <Button
                        startIcon={<Add />}
                        onClick={() => setSections([...entity.sections, emptySection()])}
                    >
                        Lägg till avsnitt
                    </Button>
                </Grid>
            </Grid>
        </FullForm>
    );
}
